import React, {useCallback, useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import moment from 'moment';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {useModal} from '../../../contexts/ModalContext';
import {notifyError, notifySuccess} from '../../../utils/notifications';
import {refreshEmailQueueSummary, useEmailQueueSummary} from '../../../utils/inquirySummary';
import {useAuth} from '../../../contexts/AuthContext';

const SILENT = {meta: {silentSuccess: true, silentError: true}};
const PAGE_SIZE = 50;

const FILTERS = [
    {value: 'problems', label: 'Problémy'},
    {value: 'waiting', label: 'Čekající'},
    {value: 'sent', label: 'Odeslané'},
    {value: 'failed', label: 'Neodeslané'},
    {value: 'expired', label: 'Prošlé'},
    {value: 'cancelled', label: 'Zrušené'},
    {value: 'all', label: 'Vše'}
];

const STATUS = {
    pending: {label: 'Čeká', className: 'bg-amber-100 text-amber-800'},
    sending: {label: 'Odesílá se', className: 'bg-blue-100 text-blue-800'},
    sent: {label: 'Odesláno', className: 'bg-emerald-100 text-emerald-800'},
    failed: {label: 'Neodesláno', className: 'bg-red-100 text-red-800'},
    expired: {label: 'Prošlé', className: 'bg-gray-200 text-gray-700'},
    cancelled: {label: 'Zrušeno', className: 'bg-gray-200 text-gray-700'}
};

const SOURCE_KINDS = {
    received: 'potvrzení přijetí',
    staffNotification: 'upozornění pro servis',
    confirmed: 'potvrzení termínu',
    rescheduled: 'změna termínu',
    cancelled: 'zrušení rezervace',
    reminder: 'připomínka',
    reply: 'odpověď',
    reviewRequest: 'žádost o recenzi',
    completionReady: 'oprava dokončena',
    resetPassword: 'obnova hesla',
    accessCreated: 'nový přístup'
};

const apiMessage = (error) => error?.response?.data?.errorMessage || error?.message || 'Akce se nepovedla.';
const formatDate = (value) => (value ? moment(value).format('D. M. YYYY H:mm') : '–');

/** Legacy záznamy bez pole status: odvodit z processed. */
const statusOf = (item) => item.status || (item.processed ? (item.error ? 'failed' : 'sent') : 'pending');

export const EmailStatusBadge = ({status}) => (
    <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${STATUS[status]?.className || 'bg-gray-100'}`}>
        {STATUS[status]?.label || status}
    </span>
);

function SourceLink({source}) {
    if (!source?.type) return <span className="text-gray-400">–</span>;
    const kind = SOURCE_KINDS[source.kind] || source.kind || '';
    if (source.type === 'webInquiry' && source.id) {
        return <Link className="text-indigo-700 hover:underline" to={routes.admin.getInquiry(source.id)}>Požadavek z webu{kind ? ` – ${kind}` : ''}</Link>;
    }
    if (source.type === 'repair' && source.id) {
        return <Link className="text-indigo-700 hover:underline" to={routes.admin.getEditRepair(source.id)}>Zakázka{kind ? ` – ${kind}` : ''}</Link>;
    }
    const type = {user: 'Uživatel', test: 'Test'}[source.type] || source.type;
    return <span>{type}{kind ? ` – ${kind}` : ''}</span>;
}

/** Přehled trvalé fronty e-mailů: co čeká, co odešlo, co se nepovedlo – a ruční zásahy. */
const EmailQueue = () => {
    const {user} = useAuth();
    const summary = useEmailQueueSummary(user?.profileList || []);
    const {openModal, confirm} = useModal();
    const [params, setParams] = useSearchParams();
    const status = params.get('stav') || 'problems';
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.emailQueue.list, {params: {status, q: search, page, limit: PAGE_SIZE}, meta: {silentError: true}});
            setData(res.data);
        } catch (e) {
            notifyError(apiMessage(e));
        }
    }, [status, search, page]);

    useEffect(() => {
        load();
        const timer = setInterval(load, 30 * 1000);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(query.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    const afterChange = () => {
        load();
        refreshEmailQueueSummary();
    };

    const retryAll = async () => {
        const ok = await confirm(`Zkusit znovu odeslat všechny neodeslané e-maily (${summary?.failed || 0})?`, {title: 'Zkusit znovu', okText: 'Ano', cancelText: 'Zpět'});
        if (ok !== true) return;
        try {
            const res = await API.post(API_ENDPOINTS.emailQueue.retryFailed, {}, SILENT);
            notifySuccess(`Znovu ve frontě: ${res.data?.retried || 0}`);
            afterChange();
        } catch (e) {
            notifyError(apiMessage(e));
        }
    };

    const openDetail = (item) => openModal(EmailDetail, {id: item._id, onChange: afterChange}, {title: item.subject});

    const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold">Fronta e-mailů</h1>
                    <p className="max-w-3xl text-sm text-gray-600">
                        Každý e-mail se nejdřív uloží a pak se odesílá – neztratí se ani při výpadku serveru.
                        Při chybě se odeslání opakuje (10 pokusů během ~28 h), potom zůstane jako neodeslaný.
                    </p>
                </div>
                {summary?.failed ? (
                    <button type="button" onClick={retryAll} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                        Zkusit znovu všechny neodeslané ({summary.failed})
                    </button>
                ) : null}
            </div>

            {summary?.problems ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {summary.failed ? <>Neodeslané e-maily: <strong>{summary.failed}</strong>. </> : null}
                    {summary.stuck ? <>Čeká déle než hodinu: <strong>{summary.stuck}</strong>. </> : null}
                    Nejčastější příčinou je výpadek nebo změna přihlášení k SMTP serveru.
                </div>
            ) : summary ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">Vše v pořádku – žádné problémy s odesíláním.</div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => {
                            setParams(item.value === 'problems' ? {} : {stav: item.value}, {replace: true});
                            setPage(1);
                        }}
                        className={`rounded-full border px-3 py-1 text-sm ${status === item.value ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                        {item.label}
                        {item.value === 'problems' && summary?.problems ? <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-xs text-white">{summary.problems}</span> : null}
                        {item.value === 'waiting' && summary?.waiting ? <span className="ml-1.5 text-xs opacity-75">{summary.waiting}</span> : null}
                    </button>
                ))}
                <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Hledat příjemce nebo předmět…"
                    className="ml-auto w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm sm:w-72"
                />
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full min-w-[56rem] text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                            <th className="px-3 py-2 font-normal">Stav</th>
                            <th className="px-3 py-2 font-normal">Příjemce</th>
                            <th className="px-3 py-2 font-normal">Předmět</th>
                            <th className="px-3 py-2 font-normal">K čemu</th>
                            <th className="px-3 py-2 font-normal">Vytvořeno</th>
                            <th className="px-3 py-2 font-normal">Odesláno / další pokus</th>
                            <th className="px-3 py-2 text-right font-normal">Pokusy</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!data ? (
                            <tr><td colSpan={7} className="p-6 text-center text-gray-500">Načítám…</td></tr>
                        ) : data.items.length ? data.items.map((item) => {
                            const itemStatus = statusOf(item);
                            return (
                                <tr key={item._id} onClick={() => openDetail(item)} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-3 py-2"><EmailStatusBadge status={itemStatus}/></td>
                                    <td className="max-w-[14rem] truncate px-3 py-2">{item.to}</td>
                                    <td className="max-w-[22rem] px-3 py-2">
                                        <span className="block truncate">{item.subject}</span>
                                        {item.error && itemStatus !== 'sent' ? <span className="block truncate text-xs text-red-700">{item.error}</span> : null}
                                    </td>
                                    <td className="px-3 py-2 text-xs" onClick={(event) => event.stopPropagation()}><SourceLink source={item.source}/></td>
                                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{formatDate(item.createdAt)}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                                        {itemStatus === 'sent'
                                            ? formatDate(item.sentAt || item.processedDate)
                                            : !item.processed && item.nextAttemptAt ? `další ${formatDate(item.nextAttemptAt)}` : '–'}
                                    </td>
                                    <td className="px-3 py-2 text-right">{item.attempts || 0}</td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan={7} className="p-6 text-center text-gray-500">{status === 'problems' ? 'Žádné problémy – všechny e-maily odcházejí.' : 'Nic neodpovídá filtru.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {data && data.total > PAGE_SIZE ? (
                <div className="flex items-center justify-end gap-2 text-sm">
                    <span className="text-gray-500">Celkem {data.total}</span>
                    <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-gray-300 bg-white px-3 py-1 disabled:opacity-40">←</button>
                    <span>{page} / {pages}</span>
                    <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded border border-gray-300 bg-white px-3 py-1 disabled:opacity-40">→</button>
                </div>
            ) : null}
        </div>
    );
};

const ATTEMPT_LABELS = {
    sent: 'Odesláno',
    failed: 'Chyba',
    expired: 'Prošlé – neodesláno',
    retry: 'Ručně znovu do fronty',
    cancelled: 'Zrušeno'
};

/** Detail e-mailu: náhled, pokusy, ruční akce. */
const EmailDetail = ({id, onChange, close}) => {
    const [item, setItem] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.emailQueue.detail(id), {meta: {silentError: true}});
            setItem(res.data);
        } catch (e) {
            notifyError(apiMessage(e));
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const act = async (url, message) => {
        setBusy(true);
        try {
            const res = await API.post(url, {}, SILENT);
            setItem(res.data);
            notifySuccess(message);
            onChange?.();
        } catch (e) {
            notifyError(apiMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!item) return <div className="w-[min(48rem,90vw)] p-4 text-sm text-gray-500">Načítám…</div>;
    const itemStatus = statusOf(item);
    const canRetry = item.processed && ['failed', 'expired', 'cancelled'].includes(itemStatus);
    const waiting = !item.processed;

    return (
        <div className="w-[min(48rem,90vw)] space-y-4">
            <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-gray-500">Stav</dt>
                <dd><EmailStatusBadge status={itemStatus}/></dd>
                <dt className="text-gray-500">Komu</dt>
                <dd className="break-all">{item.to}</dd>
                {item.replyTo ? <><dt className="text-gray-500">Odpověď na</dt><dd className="break-all">{item.replyTo}</dd></> : null}
                <dt className="text-gray-500">K čemu</dt>
                <dd><SourceLink source={item.source}/></dd>
                <dt className="text-gray-500">Vytvořeno</dt>
                <dd>{formatDate(item.createdAt)}</dd>
                {item.sentAt ? <><dt className="text-gray-500">Odesláno</dt><dd>{formatDate(item.sentAt)}</dd></> : null}
                {waiting && item.nextAttemptAt ? <><dt className="text-gray-500">Další pokus</dt><dd>{formatDate(item.nextAttemptAt)}</dd></> : null}
                <dt className="text-gray-500">Pokusy</dt>
                <dd>{item.attempts || 0}</dd>
                {item.error && itemStatus !== 'sent' ? <><dt className="text-gray-500">Poslední chyba</dt><dd className="break-words text-red-700">{item.error}</dd></> : null}
            </dl>

            <div className="flex flex-wrap gap-2">
                {canRetry ? (
                    <button type="button" disabled={busy} onClick={() => act(API_ENDPOINTS.emailQueue.retry(item._id), 'E-mail je znovu ve frontě')} className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50">
                        Zkusit znovu
                    </button>
                ) : null}
                {waiting ? (
                    <>
                        <button type="button" disabled={busy} onClick={() => act(API_ENDPOINTS.emailQueue.sendNow(item._id), 'Odesílám')} className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50">
                            Odeslat hned
                        </button>
                        <button type="button" disabled={busy} onClick={() => act(API_ENDPOINTS.emailQueue.cancel(item._id), 'Odeslání zrušeno')} className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">
                            Zrušit odeslání
                        </button>
                    </>
                ) : null}
                <button type="button" onClick={load} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Obnovit</button>
                <button type="button" onClick={() => close?.()} className="ml-auto rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Zavřít</button>
            </div>

            {item.attemptLog?.length ? (
                <div>
                    <h3 className="mb-1 text-sm font-semibold">Průběh</h3>
                    <ol className="space-y-1 text-sm">
                        {item.attemptLog.map((entry, index) => (
                            <li key={index} className="flex flex-wrap gap-x-2">
                                <span className="text-gray-500">{formatDate(entry.at)}</span>
                                <span className={entry.result === 'failed' ? 'text-red-700' : ''}>{ATTEMPT_LABELS[entry.result] || entry.result}</span>
                                {entry.user ? <span className="text-gray-500">({entry.user.name} {entry.user.surname})</span> : null}
                                {entry.error ? <span className="w-full break-words text-xs text-red-700">{entry.error}</span> : null}
                            </li>
                        ))}
                    </ol>
                </div>
            ) : null}

            <div>
                <h3 className="mb-1 text-sm font-semibold">Náhled</h3>
                {/* sandbox bez skriptů – obsah e-mailu se jen zobrazí */}
                <iframe title="Náhled e-mailu" sandbox="" srcDoc={item.html} className="h-[28rem] w-full rounded border border-gray-200 bg-white"/>
                <details className="mt-2 text-sm">
                    <summary className="cursor-pointer text-gray-600">Textová verze</summary>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs">{item.text}</pre>
                </details>
            </div>
        </div>
    );
};

export default EmailQueue;
